/* eslint-disable max-len */
import cn from 'classnames';
import React, { createContext, FC, useCallback, useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CmsPopupRenderer from 'src/components/common/CmsPopupRenderer';
import { Components } from 'src/configs/components/components';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { getHomePage } from 'src/services/cmsApi';
import { CmsPageContent } from 'src/types/cms/cmsCommonTypes';
import { CmsHomePageFields } from 'src/types/cms/cmsPageTypes';
import { RootState } from 'src/types/store-types';
import '../styles.scss';

type Props = {
  settings: any;
  type: string;
};

export const HomePageContext = createContext<CmsPageContent<CmsHomePageFields> | null>(null);

const HomePageConstructor: FC<Props> = ({ settings, type }) => {
  const [homePage, setHomePage] = useState<CmsPageContent<CmsHomePageFields> | null>(null);
  const { i18n } = useTranslation();
  const sessionRecorder = useSessionRecorder();

  const { sectionLoading, generalLoading } = useSelector((state: RootState) => state.app);
  const renderHomePageWidgets = useCallback((): JSX.Element => {
    if (!settings || typeof settings !== 'object') {
      return <></>;
    }

    const orderIndex: Record<string, number> = {};
    Object.keys(settings).forEach((key: string) => {
      orderIndex[key] = settings[key]?.priority ?? 0;
    });

    const widgetLine = Object.keys(settings)
      .sort((a: string, b: string) => orderIndex[a] - orderIndex[b])
      .filter((key: string) => key !== 'styles')
      .map((key: string, index: number) => {
        const Component = Components[key];
        if (!Component) {
          return null;
        }

        return React.createElement(Component, {
          settings: settings[key],
          type,
          key: `${key}_${index}`,
        });
      });
    return <>{widgetLine}</>;
  }, [settings, type]);

  useEffect(() => {
    sessionRecorder?.trackEvent('Home page view');
  }, []);

  useEffect(() => {
    getHomePage(i18n.language).then((res) => {
      setHomePage(res.result?.content || null);
    });
  }, [i18n.language]);

  return (
    <HomePageContext.Provider value={homePage}>
      <HelmetProvider>
        <Helmet>
          {homePage?.meta?.pageTitle && <title>{homePage.meta.pageTitle || 'Home Page'}</title>}
          {homePage?.meta?.pageDescription && (
            <meta name="description" content={homePage.meta.pageDescription || 'Home Page'} />
          )}
          {homePage?.meta?.pageKeywords && <meta name="keywords" content={homePage.meta.pageKeywords || 'Home Page'} />}
        </Helmet>
      </HelmetProvider>

      <div
        className={cn('home_container', {
          ['is-load']: !sectionLoading.includes(false),
        })}
      >
        {renderHomePageWidgets()}
        {generalLoading.includes(false) && <CmsPopupRenderer popup={homePage?.pageFields.popup?.[0] || null} />}
      </div>
    </HomePageContext.Provider>
  );
};
export default HomePageConstructor;
